import React, { useState, useContext } from "react";
import {
  Grid,
  Table,
  Checkbox,
  PaginationProps,
  Icon,
  Pagination,
} from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "../../../app/stores/rootStore";
import { IWarranty } from "../../../app/models/warranty";
import WarrantyDetails from "./WarrantyDetails";
import { CSVLink } from "react-csv";

import "../../../app/layout/styles.css";

const WarrantyList: React.FC = () => {
  const rootStore = useContext(RootStoreContext);
  const {
    warrantiesByDate,
    isShowWarrantyDetail,
    setShowWarrantyDetail,
    columnToSort,
    sortingOrder,
    setColumnToSorting,
    setSortingOrder,
    editWarranty,
    setIsSelectAll,
    isSelectAll,
    setDefaultActivePageSize,
    startRecord,
    endRecord,
  } = rootStore.warrantyStore;

  const [selectedWarranty, setSelectedWarranty] = useState<IWarranty | null>(
    null
  );

  const handleSelectWarranty = (id: number) => {
    let selectedWarranty = warrantiesByDate.find((x) => x.warranty_id === id);

    setSelectedWarranty(selectedWarranty as IWarranty);

    setShowWarrantyDetail(true);
  };

  const invertDirection = (direction: string) => {
    if (direction === "ascending") return "descending";
    else return "ascending";
  };

  const invertDirectionTable = (direction: string, col: string) => {
    if (direction === "ascending" && columnToSort === col) return "ascending";
    else if (direction === "descending" && columnToSort === col)
      return "descending";
    else return null as any;
  };

  const handleSortDate = (columnName: string) => {
    setColumnToSorting(columnName);
    setSortingOrder(
      columnToSort === columnName ? invertDirection(sortingOrder) : "ascending"
    );
  };

  const toggleSelect = (warrantyData: any) => {
    editWarranty(warrantyData);
  };

  function btnPageClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) {
    setDefaultActivePageSize(data.activePage as number);
  }

  const headers = [
    //{ label: "ID", key: "firstname" },
    { label: "First Name", key: "warranty_firstname" },
    { label: "Last Name", key: "warranty_lastname" },
    { label: "Address 1", key: "warranty_address" },
    { label: "Address 2", key: "warranty_address1" },
    { label: "Suburb", key: "warranty_suburb" },
    { label: "State", key: "warranty_state" },
    { label: "Postcode", key: "warranty_postcode" },
    { label: "Contact number", key: "warranty_contact_number" },
    { label: "Email", key: "warranty_email" },
    { label: "Category", key: "warranty_category" },
    { label: "SKU", key: "warranty_sku" },
    { label: "Barcode", key: "warranty_barcode" },
    { label: "Website", key: "warranty_website" },
    { label: "Subscribe", key: "warranty_subscribe" },
    { label: "Purchased date", key: "warranty_purchased_date" },
    { label: "Register data", key: "warranty_createdAt" },
    { label: "Register site", key: "warranty_register_site" },
    { label: "Period (Months)", key: "warranty_new_warranty_period" },
    { label: "End Date", key: "warranty_warranty_end_date" },
  ];

  return (
    <Grid>
      <Grid.Row className="noPadding">
        <Grid.Column width={11} className="clearing">
          <CSVLink
            data={warrantiesByDate.filter((wir) => wir.isSelected)}
            headers={headers}
            title="Download List"
            className="floatRight"
            filename={"warranty_registration_data.csv"}
          >
            <Icon
              circular
              inverted
              color="teal"
              name="download"
              size="large"
              floated="right"
            />
          </CSVLink>
        </Grid.Column>
        <Grid.Column width={5}></Grid.Column>
      </Grid.Row>
      <Grid.Row className="noPadding">
        <Grid.Column width={11}>
          <Table sortable celled selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>
                  <Checkbox
                    slider
                    checked={isSelectAll}
                    name={"chkSelectAll"}
                    onChange={() => setIsSelectAll(!isSelectAll)}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={invertDirectionTable(
                    sortingOrder,
                    "warranty_firstname"
                  )}
                  onClick={() => handleSortDate("warranty_firstname")}
                >
                  First Name
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={invertDirectionTable(
                    sortingOrder,
                    "warranty_lastname"
                  )}
                  onClick={() => handleSortDate("warranty_lastname")}
                >
                  Last Name
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={invertDirectionTable(sortingOrder, "warranty_sku")}
                  onClick={() => handleSortDate("warranty_sku")}
                >
                  SKU
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={invertDirectionTable(
                    sortingOrder,
                    "warranty_purchased_dateDisplay"
                  )}
                  onClick={() =>
                    handleSortDate("warranty_purchased_dateDisplay")
                  }
                >
                  Purchased Date
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={invertDirectionTable(
                    sortingOrder,
                    "warranty_createdAtDisplay"
                  )}
                  onClick={() => handleSortDate("warranty_createdAtDisplay")}
                >
                  Registered Date
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {warrantiesByDate
                .slice(startRecord, endRecord)
                .map((warranty, key) => (
                  <Table.Row
                    key={key}
                    onClick={() => handleSelectWarranty(warranty.warranty_id)}
                    active={
                      warranty.warranty_id === selectedWarranty?.warranty_id
                    }
                  >
                    <Table.Cell>
                      <Checkbox
                        slider
                        checked={warranty.isSelected}
                        name={"chk_" + key}
                        onChange={() => toggleSelect(warranty)}
                      />
                    </Table.Cell>
                    <Table.Cell>{warranty.warranty_firstname}</Table.Cell>
                    <Table.Cell>{warranty.warranty_lastname}</Table.Cell>
                    <Table.Cell>{warranty.warranty_sku}</Table.Cell>
                    <Table.Cell>
                      {warranty.warranty_purchased_dateDisplay}
                    </Table.Cell>
                    <Table.Cell>
                      {warranty.warranty_createdAtDisplay}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
            <Table.Footer fullWidth>
              <Table.Row>
                <Table.HeaderCell colSpan="6">
                  <Pagination
                    defaultActivePage={1}
                    totalPages={Math.ceil(warrantiesByDate.length / 10)}
                    onPageChange={btnPageClick}
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
          {/*   <Table<IWarranty>
          name={"warrantiesResgistrationTable"}
          columns={columns}
          //data={warrantiesByDate}
          data={warrantiesByDate}
          onDelete={dummy}
          selectWarranty={handleSelectWarranty}
          handleSort={handleSort}
        /> */}
        </Grid.Column>
        <Grid.Column width={5}>
          {selectedWarranty && isShowWarrantyDetail && (
            <WarrantyDetails warranty={selectedWarranty} />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default observer(WarrantyList);
